import Vue from 'vue';
import Router from 'vue-router';

import auth from '@/services/authSvc.js';
import { reauthFn } from '@/services/baseHttpSvc';

import Dashboard from '@/components/Dashboard';
import Patient from '@/components/Patient';
import SinglePatient from '@/components/SinglePatient';
import Coaching from '@/components/Coaching';
import CoachingCannedResponseManager from '@/components/coaching/CoachingCannedResponseManager';
import Report from '@/components/Report';
import Search from '@/components/Search';
import NotFound from '@/components/NotFound';
import Unauthorized from '@/components/Unauthorized';
import ClinicalPortalView from '@/components/ClinicalPortalView.vue';

Vue.use(Router);

// Set routerMode to hash for development - for nested route livereload
const routerMode = process.env.NODE_ENV === 'development' ? 'hash' : 'history';

const router = new Router({
  mode: routerMode,
  routes: [
    {
      path: '/',
      name: 'Default'
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: Dashboard,
      meta: { permissions: [auth.permission.Dashboard] }
    },
    {
      path: '/clinical-portal-view',
      name: 'ClinicalPortalView',
      component: ClinicalPortalView
    },

    {
      path: '/patient/:id',
      name: 'Patient',
      props: true,
      component: Patient,
      meta: {
        permissions: [
          auth.permission.Dashboard,
          auth.permission.Coach,
          auth.permission.CoachSupervisor
        ]
      }
    },
    {
      path: '/coaching',
      name: 'Coaching',
      component: Coaching,
      meta: {
        permissions: [auth.permission.Coach, auth.permission.CoachSupervisor]
      }
    },
    {
      path: '/coaching-canned-response-manager',
      name: 'Coaching Canned Response Manager',
      component: CoachingCannedResponseManager,
      meta: {
        permissions: [auth.permission.Coach, auth.permission.CoachSupervisor]
      }
    },
    {
      path: '/report',
      name: 'Report',
      component: Report,
      meta: { permissions: [auth.permission.Dashboard] }
    },
    {
      path: '/search',
      component: Search,
      meta: { permissions: [auth.permission.Dashboard] }
    },
    {
      path: '/404',
      name: 'NotFound',
      component: NotFound
    },
    {
      path: '/404',
      name: 'SinglePatientNotFound',
      component: NotFound,
      props: { isSinglePatient: true }
    },
    {
      path: '/unauthorized',
      name: 'Unauthorized',
      component: Unauthorized
    },
    {
      path: '/single-patient/:id',
      name: 'ssoPatient',
      props: true,
      component: SinglePatient,
      meta: { permissions: [auth.permission.DashboardSinglePatient] }
    },
    {
      path: '*',
      name: 'Fallback'
    }
  ]
});

// add global navigation guard
router.beforeEach(async (to, from, next) => {
  const userToken = auth.getToken();
  if (to.path === '/404') {
    return next();
  }
  if (userToken == null) {
    if (to.name === 'ClinicalPortalView') {
      return next();
    } else if (to.name === 'ssoPatient') {
      return next({ name: 'SinglePatientNotFound' });
    } else {
      const reauthToken = auth.getReauthToken();
      if (reauthToken !== null) {
        await attemptReauth(reauthToken);
      } else {
        auth.returnToLogin();
      }
    }

    return next();
  }
  if (auth.getPermissions().length === 0) {
    auth.setPermissions();
  }

  if (!auth.isVerified() && to.name !== 'ssoPatient') {
    auth.requestAuthCode();
    return next();
  }

  let redirect = getRedirect(to);
  if (redirect) {
    return next(redirect);
  } else {
    return next();
  }
});

async function attemptReauth(reauthToken) {
  try {
    await reauthFn(reauthToken).then(() => {
      location.reload();
    });
  } catch (error) {
    auth.returnToLogin();
  }
}

function getRedirect(route) {
  if (route.name === 'Default') {
    // set default route based upon user type
    return auth.isCoach() ? '/coaching' : '/dashboard';
  }
  if (route.name === 'Fallback') {
    return auth.isCoach() ? '/coaching' : '/report';
  }
  if (
    !route.matched.some(record => auth.hasPermission(record.meta.permissions))
  ) {
    return '/unauthorized';
  }
  return null;
}

export default router;
